import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import GetAppIcon from "@mui/icons-material/GetApp";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface PWAUpdateNotificationProps {
  onInstall?: () => void;
  onUpdate?: () => void;
}

const floatingAlertSx = {
  position: "fixed" as const,
  bottom: 16,
  right: 16,
  width: 320,
  zIndex: 50,
  boxShadow: 3,
};

export const PWAUpdateNotification: React.FC<PWAUpdateNotificationProps> = ({
  onInstall,
  onUpdate,
}) => {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowInstallPrompt(true);
    };

    const handleSWUpdate = () => {
      setShowUpdatePrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener(
        "controllerchange",
        handleSWUpdate,
      );
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener(
          "controllerchange",
          handleSWUpdate,
        );
      }
    };
  }, []);

  const handleInstallClick = () => {
    void (async () => {
      if (!deferredPrompt) {
        return;
      }

      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === "accepted") {
        setShowInstallPrompt(false);
        onInstall?.();
      }

      setDeferredPrompt(null);
    })();
  };

  const handleUpdateClick = () => {
    if ("serviceWorker" in navigator) {
      void navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration?.waiting) {
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
          window.location.reload();
        }
      });
    }
    setShowUpdatePrompt(false);
    onUpdate?.();
  };

  if (showInstallPrompt) {
    return (
      <Alert
        severity="info"
        icon={<GetAppIcon fontSize="inherit" />}
        sx={{
          ...floatingAlertSx,
          bgcolor: "background.paper",
          border: 2,
          borderColor: "primary.main",
        }}
        action={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button
              size="small"
              variant="contained"
              onClick={handleInstallClick}
            >
              Install
            </Button>
            <IconButton
              size="small"
              aria-label="Dismiss install prompt"
              onClick={() => setShowInstallPrompt(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
      >
        <Typography variant="body2">
          Install CTMS app for quick access
        </Typography>
      </Alert>
    );
  }

  if (showUpdatePrompt) {
    return (
      <Alert
        severity="info"
        icon={<SystemUpdateAltIcon fontSize="inherit" />}
        sx={(theme) => ({
          ...floatingAlertSx,
          bgcolor: alpha(theme.palette.info.main, 0.08),
          border: 2,
          borderColor: "info.light",
        })}
        action={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button
              size="small"
              variant="contained"
              onClick={handleUpdateClick}
            >
              Update
            </Button>
            <IconButton
              size="small"
              aria-label="Dismiss update prompt"
              onClick={() => setShowUpdatePrompt(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
      >
        <Box>
          <Typography variant="body2">New version available</Typography>
        </Box>
      </Alert>
    );
  }

  return null;
};

export default PWAUpdateNotification;
